import compact from 'lodash/compact';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { displayLibxRegions } from '../../../store/header/actions';
import ContentBaseLayout from '../shared/ContentBaseLayout';
import ProductList from '../../../shared/common/ProductList';
import LibxchangeProductElement from '../shared/LibxchangeProductElement';
import { getPaginationStateObject, Pagination } from '../../../shared/common/Pagination';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../../shared/components/NoElementsFound';
import { LocalRegionsSidebar } from './LocalRegionsSidebar';
import { useHistory, useLocation, useParams } from 'react-router';
import {
    invalidateLocalRegionProducts,
    useLocalRegionProducts
} from '../../../api/local-content/marketplace/local-regions/useLocalRegionProducts';
import { useLocalRegions } from '../../../api/local-content/marketplace/local-regions/useLocalRegions';
import { ageCategoryFromString } from '../../../../models/AgeCategory';
import queryString from 'query-string';
import { useQueryParams } from '../../../shared/hooks/useQueryParams';
import { ProductSorting } from '../../../../models/Product';
import { deserializeFilter } from '../../../shared/utils/deserializeFilter';
import { setSelectedLocalRegion } from '../../../store/libx/actions';
import { BreadcrumbsState } from '../../../store/breadcrumbs/state';
import { DISPLAY_TYPE_MINIMAL, DISPLAY_TYPE_TILE } from '../../../store/displayType/types';
import { FilterBar } from '../../../shared/common/FilterBar';
import { modifyURLState, modifyURLStates } from '../../../shared/utils/utils-routing';
import { useAddLibxProductToCollection } from '../productdetail/api/useAddLibxProductToCollection';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { fetchLocalRegionsAggregationsQuickSearch } from '../../../api/local-content/marketplace/local-regions/fetchLocalRegionsAggregationsQuickSearch';
import BreadCrumbNav from '../../../shared/navigation/BreadcrumbNav';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { fetchLocalRegionsAggregations } from '../../../api/local-content/marketplace/local-regions/fetchLocalRegionsAggregations';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { AppState } from '../../../store/appState';
import { defaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';

const StFilterBar = styled(FilterBar)`
    padding: 25px 0;
`;

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';

export const DEFAULT_PAGE_SIZE = 55;

export const LocalRegionsPage = () => {
    const dispatch = useDispatch();
    const authentication = useSelector((state: AppState) => state.authentication);
    const [selectedRegion, setSelectedRegion] = useState<string | undefined>(undefined);
    const { level1, level2 } = useParams<{ level1: string; level2: string }>();

    const { search } = useLocation();
    const history = useHistory();

    useLayoutEffect(() => {
        dispatch(displayLibxRegions());
    }, [dispatch]);

    /**
     * The `displayFilter` query param is either there or not. There is no value attached to it.
     * Examples:
     *      with:       `/collection/by-genre/all?displayFilter
     *      without:    `/collection/by-genre/all
     *
     * Parsing this query param with the `query-string` library will result in either null value or undefined.
     * This is the way how we can identify if the filter should be open or not.
     * Unfortunately, we can not replicate this behaviour with the `useQueryParams` hook.
     *
     * Of course it would be nicer to have a boolean value.
     * But that is a breaking change in regard to the URL structure.
     */
    const { displayFilter: displayFilterQueryParam } = queryString.parse(search);
    const displayFilter = displayFilterQueryParam === null;

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },
        filter: { value: filterQueryParam },
        ageCategory: { value: ageCategoryQueryParam },
        display: { value: display }
    } = useQueryParams({
        [PAGE_QUERY_PARAM]: 'integer',
        [SORT_BY_QUERY_PARAM]: 'string',
        pageSize: 'integer',
        filter: 'string',
        ageCategory: 'string',
        display: 'string'
    });
    const page = pageParam - 1 ?? 0;
    const sortBy = sortByParam ?? ProductSorting.RELEASE_DATE_NEW_TO_OLD;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const ageCategory = ageCategoryFromString(ageCategoryQueryParam);
    const filter = deserializeFilter(filterQueryParam);

    const { localRegions, isLoading: isLocalRegionsLoading } = useLocalRegions(ageCategory);

    const { mutate: addProductToCollection } = useAddLibxProductToCollection();

    const {
        products,
        totalHits,
        isInitialLoading: isProductsLoading
    } = useLocalRegionProducts({
        pagination: { sortBy, page, pageSize },
        filterMap: filter,
        enabled: !!selectedRegion,
        ageCategory,
        region: selectedRegion ?? localRegions[0]?.name
    });

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => defaultVisibleFilterGroups(authentication, ageCategory),
        [ageCategory, authentication]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'localRegionsPage',
            selectedRegion ?? localRegions[0]?.name,
            'aggregation',
            ageCategory,
            filters,
            expandedFilterGroups
        ],
        [ageCategory, localRegions, selectedRegion]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options =>
            fetchLocalRegionsAggregations({ ...options, ageCategory, region: selectedRegion ?? localRegions[0]?.name }),
        [ageCategory, localRegions, selectedRegion]
    );
    const fetchAggregationsQuickSearch = useCallback<FetchAggregationsQuickSearch>(
        options =>
            fetchLocalRegionsAggregationsQuickSearch({
                ...options,
                ageCategory
            }),
        [ageCategory]
    );
    //endregion

    const breadcrumbRemap = localRegions.map(region => ({
        key: region.name,
        value: region.name,
        docCount: region.docCount,
        buckets: region?.level2?.map(level2 => ({
            key: !selectedRegion?.includes(level2?.name!) ? `${level1}->${level2.name}` : selectedRegion,
            value: level2.name,
            docCount: level2.docCount ?? 0
        }))
    }));

    const breadcrumbsState: BreadcrumbsState = {
        key: 'by-region',
        label: 'Local Regions',
        buckets: (breadcrumbRemap as any) || [],
        activeBucket: selectedRegion
    };

    const loading = isLocalRegionsLoading || isProductsLoading;

    const syncUrlToState = useCallback(
        (level1, level2, localRegions) => {
            let selectedLocalRegion = compact([level1, level2]).join('->');
            if (!selectedLocalRegion && localRegions.length > 0) {
                selectedLocalRegion = localRegions[0]?.name;
            }

            setSelectedRegion(selectedLocalRegion);
            dispatch(setSelectedLocalRegion(selectedLocalRegion));
        },
        [dispatch]
    );

    useEffect(() => {
        syncUrlToState(level1, level2, localRegions);
    }, [level1, level2, localRegions, selectedRegion, syncUrlToState]);

    function handleAddToCollection(productId) {
        addProductToCollection(productId);
        invalidateLocalRegionProducts(selectedRegion).then();
    }

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: SORT_BY_QUERY_PARAM, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(history.location.search, [pageQueryState, sortByQueryState]);

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const setPage = nextPage => {
        const nextSearch = modifyURLState(
            history.location.search,
            PAGE_QUERY_PARAM,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(history.location.search, 'displayFilter', !displayFilter, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function onDisplayChange(nextDisplayType) {
        const nextSearch = modifyURLState(history.location.search, 'display', nextDisplayType, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    return (
        <ContentBaseLayout
            documentTitle="Local Regions"
            topComponent={
                <>
                    <BreadCrumbNav
                        breadcrumbs={breadcrumbsState}
                        fixedLabel="Local Regions"
                        path="libxchange"
                        customGenreBase="localregions"
                        floatingClass=""
                    />
                    <StFilterBar
                        onSortChange={setSortBy}
                        dispatch={dispatch}
                        totalProducts={totalHits}
                        currentPage={page}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        displayType={display ?? DISPLAY_TYPE_MINIMAL}
                        sortBy={sortBy}
                        onFilterDisplayToggle={onFilterDisplayToggle}
                        onDisplayChange={onDisplayChange}
                        hideSelectButtons={true}
                        hideGroupBy={true}
                        excludeDisplayTypes={[DISPLAY_TYPE_TILE]}
                        excludeSorting={[
                            'narratorAZ',
                            'stock',
                            'reserve',
                            'durationLongToShort',
                            'durationShortToLong',
                            'priceHighToLow',
                            'priceLowToHigh'
                        ]}
                        displayFilter={displayFilter}
                    />
                </>
            }
            sidebarComponent={
                selectedRegion && !displayFilter ? (
                    <LocalRegionsSidebar localRegions={localRegions} selectedRegion={selectedRegion} />
                ) : (
                    <ResponsiveFilterBar
                        visibleFilterGroups={visibleFilterGroups}
                        buildQueryKey={aggregationsQueryKeyBuilder}
                        fetchAggregations={fetchAggregations}
                        fetchAggregationsQuickSearch={fetchAggregationsQuickSearch}
                    />
                )
            }
            contentComponent={
                !loading && products.length === 0 ? (
                    <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                ) : (
                    <div className="ca-main">
                        <div className="ca-main-inner">
                            <ProductList
                                listItemComponent={LibxchangeProductElement}
                                tileItemComponent={LibxchangeProductElement}
                                onAddToCollection={handleAddToCollection}
                                products={products}
                                display={display}
                                dispatch={dispatch}
                                totalProducts={totalHits}
                                displayType={display}
                                currentPage={page}
                            />
                            <Pagination
                                totalItems={totalHits}
                                currentPage={page}
                                onPageChange={setPage}
                                pageSize={pageSize}
                            />
                        </div>
                    </div>
                )
            }
            loading={loading}
        />
    );
};
